import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MAGAZINE_ITEMS as FALLBACK_ITEMS, MARKETS } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* MAGAZINE + MARKETS CALENDAR — SEO-friendly curated feed */
export default function Magazine() {
  /* Dynamically compute upcoming markets from ISO dates.
     Concluded events are auto-hidden; next upcoming is featured. */
  const { nextMarket, upcoming, allDone } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const future = MARKETS
      .filter((m) => {
        const end = m.endDate ? new Date(m.endDate) : null;
        return end && end >= today;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    if (future.length === 0) {
      return { nextMarket: null, upcoming: [], allDone: true };
    }
    return { nextMarket: future[0], upcoming: future.slice(1, 8), allDone: false };
  }, []);

  const [items, setItems] = useState(FALLBACK_ITEMS);
  const [meta, setMeta] = useState({ updatedAt: null, source: "fallback" });
  const [loading, setLoading] = useState(true);
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetPreselect, setMeetPreselect] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/magazine`, { timeout: 20000 });
        if (cancelled) return;
        const live = res.data?.items || [];
        if (live.length > 0) {
          setItems(live);
          setMeta({ updatedAt: res.data?.updated_at, source: res.data?.source || "live" });
        }
      } catch (e) {
        // fallback silently to curated seed items
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updatedLabel = React.useMemo(() => {
    if (!meta.updatedAt) return "";
    try {
      const d = new Date(meta.updatedAt);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
      });
    } catch { return ""; }
  }, [meta.updatedAt]);

  return (
    <section id="magazine" className="vw-section relative" data-testid="vw-magazine">
      <div className="vw-container">
        {/* Header */}
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="vw-eyebrow mb-6 flex items-center gap-3 flex-wrap">
              <span>MAGAZINE · CURATED FROM THE TRADES</span>
              {meta.source !== "fallback" && updatedLabel && (
                <span className="inline-flex items-center gap-1.5 border border-[var(--vw-cyan)] text-[var(--vw-cyan)] px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.22em]" data-testid="vw-magazine-live-badge">
                  <span className="w-1 h-1 bg-[var(--vw-cyan)] vw-blink" />
                  LIVE · UPDATED {updatedLabel}
                </span>
              )}
              {loading && (
                <span className="inline-flex items-center gap-1.5 text-[var(--vw-text-mute)] text-[9px] font-mono uppercase tracking-[0.22em]" data-testid="vw-magazine-loading">
                  <span className="w-1 h-1 bg-[var(--vw-text-mute)] vw-blink" />
                  FETCHING TRADES…
                </span>
              )}
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight max-w-[18ch]">
              The stories moving <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">our industry</span>.
            </h2>
          </div>
          <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
            A live selection from the trade press we actually read — Variety, The Hollywood Reporter, Cartoon Brew, Animation Magazine, Deadline, Screen International — auto-updated every two hours with citations, so the credit stays where it belongs.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10">
          {/* News feed */}
          <div className="space-y-3">
            {items.slice(0, 12).map((item, i) => (
              <motion.a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                key={item.id || item.testId || item.url || i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="group relative flex items-start gap-6 p-6 border border-[var(--vw-border)] hover:border-[var(--vw-cyan)] bg-[var(--vw-bg-soft)] hover:bg-[var(--vw-surface)] transition-all"
                data-testid={`vw-magazine-item-${i}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] pt-1 shrink-0 w-16">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2 font-mono text-[10px] uppercase tracking-[0.24em]">
                    <span className="text-[var(--vw-cyan)]">{item.outlet}</span>
                    {item.category && (
                      <>
                        <span className="w-3 h-px bg-[var(--vw-border-strong)]" />
                        <span className="text-[var(--vw-text-mute)]">{item.category}</span>
                      </>
                    )}
                    {item.date && (
                      <>
                        <span className="w-3 h-px bg-[var(--vw-border-strong)]" />
                        <span className="text-[var(--vw-text-mute)]">{item.date}</span>
                      </>
                    )}
                  </div>
                  <h3 className="font-display text-lg md:text-xl leading-tight text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors mb-2 pr-8">
                    {item.title}
                  </h3>
                  {item.summary && (
                    <p className="text-sm text-[var(--vw-text-dim)] leading-relaxed line-clamp-2">
                      {item.summary}
                    </p>
                  )}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--vw-text-mute)] group-hover:text-[var(--vw-cyan)] transition-colors shrink-0 mt-1">
                  <path d="M7 17L17 7M8 7h9v9"/>
                </svg>
              </motion.a>
            ))}
            <div className="pt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
              * All headlines link to source. VawWorld is not affiliated with the linked publications. Feed auto-refreshes every 2 hours.
            </div>
          </div>

          {/* Markets calendar */}
          <aside className="lg:sticky lg:top-28 self-start" data-testid="vw-markets-calendar">
            <div className="border border-[var(--vw-border-strong)] bg-[var(--vw-bg-soft)]">
              {/* Header */}
              <div className="p-5 border-b border-[var(--vw-border)] flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">MARKETS · UPCOMING</div>
                  <div className="font-display text-xl leading-tight text-[var(--vw-text)] mt-1">Buyer Calendar</div>
                </div>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]">
                  <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
                  LIVE
                </span>
              </div>

              {allDone ? (
                <div className="p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2">
                    NEXT SEASON · CALENDAR REFRESHING
                  </div>
                  <div className="font-display text-lg leading-tight text-[var(--vw-text)]">
                    The next-cycle market dates are being locked in. Drop us a line and we'll route you to the room where the deals happen.
                  </div>
                </div>
              ) : (
                <>
                  {/* Next up — featured */}
                  <button
                    type="button"
                    onClick={() => { setMeetPreselect(nextMarket.name); setMeetOpen(true); }}
                    className="block w-full text-left p-5 border-b border-[var(--vw-border)] bg-gradient-to-br from-[color:rgba(0,229,255,0.06)] to-transparent group hover:bg-[var(--vw-surface)] transition-colors"
                    data-testid={`vw-market-next-${nextMarket.id}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2">
                      NEXT UP · WE'LL BE THERE
                    </div>
                    <div className="font-display text-2xl leading-[1] tracking-tight text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors">
                      {nextMarket.name}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px] text-[var(--vw-text-dim)]">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)]">DATES</div>
                        {nextMarket.dates}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)]">CITY</div>
                        {nextMarket.city}
                      </div>
                    </div>
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)]">
                      {nextMarket.focus}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] group-hover:translate-x-0.5 transition-transform">
                      Request Meeting →
                    </div>
                  </button>

                  {/* Upcoming list */}
                  <ul>
                    {upcoming.map((m, i) => (
                      <li key={m.id} className="border-b border-[var(--vw-border)] last:border-b-0">
                        <button
                          type="button"
                          onClick={() => { setMeetPreselect(m.name); setMeetOpen(true); }}
                          className="w-full text-left p-4 flex items-start gap-4 group hover:bg-[var(--vw-surface)] transition-colors"
                          data-testid={`vw-market-${m.id}`}
                        >
                          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] pt-1 w-10 shrink-0">
                            {String(i + 2).padStart(2, "0")}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-base leading-tight text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors truncate">
                              {m.name}
                            </div>
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                              {m.dates} · {m.city}
                            </div>
                          </div>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--vw-text-mute)] group-hover:text-[var(--vw-cyan)] transition-colors shrink-0 mt-1.5">
                            <path d="M5 12h14M13 6l6 6-6 6"/>
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <button
                type="button"
                onClick={() => { setMeetPreselect(nextMarket?.name || ""); setMeetOpen(true); }}
                className="w-full block p-4 bg-black/30 border-t border-[var(--vw-border-strong)] font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] hover:bg-[var(--vw-cyan)] hover:text-black transition-colors text-center"
                data-testid="vw-market-book-meeting"
              >
                Book a Meeting at a Market →
              </button>
            </div>

            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] leading-relaxed">
              Meet us at Kidscreen, Berlinale, MIP London, Cannes, Annecy, ACE Fair Korea, MIPJunior, MIPCOM &amp; AFM. We travel with slate decks and calendar space.
            </p>
          </aside>
        </div>
      </div>

      <MeetAtMarketModal
        open={meetOpen}
        onClose={() => setMeetOpen(false)}
        markets={[nextMarket, ...upcoming].filter(Boolean)}
        preselect={meetPreselect}
      />
    </section>
  );
}

/* ─── Meet-at-Market modal ───────────────────────────────────── */
function MeetAtMarketModal({ open, onClose, markets, preselect }) {
  const [name, setName] = useState("");
  const [studio, setStudio] = useState("");
  const [email, setEmail] = useState("");
  const [market, setMarket] = useState(preselect || "");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setMarket(preselect || (markets?.[0]?.name || ""));
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open, preselect, markets]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !studio.trim()) {
      setErrorMsg("Name, studio and email are required.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const brief = `[Meet-at-Market request · ${market || "TBD"}]\n\n${message || "Please share a calendar hold — I'd like to meet the team on the floor."}`;
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        studio: studio.trim(),
        email: email.trim(),
        country: "—",
        project_type: `Meeting Request · ${market || "Market TBD"}`,
        brief,
      }, { timeout: 15000 });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "We couldn't send the request. Please email kumar@vawworld.com directly.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="vw-meet-market-modal"
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-lg" onClick={onClose} />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-[var(--vw-surface)] border border-[var(--vw-border-strong)]"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center bg-black/40 border border-[var(--vw-border-strong)] hover:border-[var(--vw-cyan)] hover:text-[var(--vw-cyan)] transition-colors"
              aria-label="Close"
              data-testid="vw-meet-modal-close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18L18 6"/></svg>
            </button>

            {status === "done" ? (
              <div className="p-8 md:p-10">
                <div className="vw-eyebrow mb-4" style={{ color: "var(--vw-cyan)" }}>REQUEST SENT</div>
                <h3 className="font-display text-3xl leading-tight text-[var(--vw-text)] mb-4">
                  We'll be in touch <span className="font-serif-italic text-[var(--vw-cyan)]">before the floor opens</span>.
                </h3>
                <p className="text-[var(--vw-text-dim)] leading-relaxed">
                  Thanks {name.split(" ")[0]}. Our team will confirm a calendar hold for <strong className="text-[var(--vw-text)]">{market || "the market"}</strong> and share our slate deck by email within one business day.
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 vw-btn-ghost"
                  data-testid="vw-meet-modal-done"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8 md:p-10">
                <div className="vw-eyebrow mb-4">MEET US AT A MARKET</div>
                <h3 className="font-display text-3xl leading-tight text-[var(--vw-text)] mb-3">
                  Hello — let's book a <span className="font-serif-italic text-[var(--vw-cyan)]">calendar hold</span>.
                </h3>
                <p className="text-sm text-[var(--vw-text-dim)] leading-relaxed mb-6">
                  Send us a short note and we'll confirm a slot on the floor. Slate deck lands in your inbox before we shake hands.
                </p>

                <div className="space-y-4">
                  <FieldWrap label="Full Name *">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="vw-input"
                      placeholder="Your name"
                      required
                      data-testid="vw-meet-name"
                    />
                  </FieldWrap>
                  <FieldWrap label="Studio / Company *">
                    <input
                      type="text"
                      value={studio}
                      onChange={(e) => setStudio(e.target.value)}
                      className="vw-input"
                      placeholder="Your company"
                      required
                      data-testid="vw-meet-studio"
                    />
                  </FieldWrap>
                  <FieldWrap label="Executive Email *">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="vw-input"
                      placeholder="you@studio.com"
                      required
                      data-testid="vw-meet-email"
                    />
                  </FieldWrap>
                  <FieldWrap label="Which Market">
                    <select
                      value={market}
                      onChange={(e) => setMarket(e.target.value)}
                      className="vw-input"
                      data-testid="vw-meet-market-select"
                    >
                      {(markets || []).map((m) => (
                        <option key={m.id} value={m.name} className="bg-[var(--vw-surface)]">
                          {m.name} — {m.dates}
                        </option>
                      ))}
                    </select>
                  </FieldWrap>
                  <FieldWrap label="Message">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="vw-input min-h-[90px] resize-y"
                      placeholder="Hi — I'd love to meet the team on the floor and see the slate. Any early morning slot works…"
                      data-testid="vw-meet-message"
                    />
                  </FieldWrap>
                </div>

                {status === "error" && (
                  <div className="mt-4 border border-[var(--vw-orange)]/40 bg-[color:rgba(255,138,71,0.06)] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--vw-orange)]" data-testid="vw-meet-error">
                    {errorMsg || "Something went wrong."}
                  </div>
                )}

                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="submit"
                    className="vw-btn-primary vw-btn-glow"
                    disabled={status === "sending"}
                    data-testid="vw-meet-submit"
                  >
                    {status === "sending" ? "Sending…" : "Send Meeting Request"}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="vw-btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function FieldWrap({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
